import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ScanLine, Undo2 } from 'lucide-react'
import { getLocations, getProducts } from '../../api/master'
import { getAreas, resolveProductByCode } from '../../api/inventory'
import { getPO, getReceipt, getReceiptAvailability, listReceipts, saveVendorReturn } from '../../api/purchase'
import { getConfig } from '../../api/settings'
import { useToast } from '../../components/Toast.jsx'
import ErrorBanner from '../../components/ErrorBanner'
import { ScanButton } from '../../components/BarcodeScanner'
import { BackLink, DocHeader } from '../../components/PageParts'
import { fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import { useVendorNames } from './purchaseShared'

const isOn = (v) => [true, 1, '1', 'true', 'yes', 'on'].includes(v)

// Send goods back to a vendor from a receipt. The receipt's items fill in;
// at least one item must be scanned before saving, as a physical check.
export default function VendorReturnForm() {
  const nav = useNavigate()
  const [params] = useSearchParams()
  const { notify } = useToast()
  const vendorNames = useVendorNames()
  const [receipts, setReceipts] = useState([])
  const [receiptId, setReceiptId] = useState(params.get('receipt_id') || '')
  const [products, setProducts] = useState([])
  const [locations, setLocations] = useState([])
  const [locationId, setLocationId] = useState('')
  const [bins, setBins] = useState([])
  const [binTracking, setBinTracking] = useState(false)
  const [items, setItems] = useState([]) // { product_id, sku, name, received, available, qty, unit_cost, reason, area_id }
  const [notes, setNotes] = useState('')
  const [scanCode, setScanCode] = useState('')
  const [scanned, setScanned] = useState(false)
  const [scanError, setScanError] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const scanRef = useRef(null)
  const qtyRefs = useRef({})

  useEffect(() => {
    listReceipts().then((all) => {
      const rows = Array.isArray(all) ? all : []
      setReceipts(rows)
      // Coming from an order without a receipt: use its latest receipt.
      if (!params.get('receipt_id') && params.get('po_id')) {
        const latest = rows.filter((r) => String(r.po_id) === String(params.get('po_id'))).sort((a, b) => num(b.receipt_id) - num(a.receipt_id))[0]
        if (latest) setReceiptId(String(latest.receipt_id))
      }
    }).catch(() => setReceipts([]))
    getProducts().then((ps) => setProducts(ps || [])).catch(() => {})
    getLocations().then((ls) => { setLocations(ls || []); if (ls?.length) setLocationId(String(ls[0].location_id)) }).catch(() => {})
    getConfig().then((c) => setBinTracking(isOn(c?.bin_tracking))).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const receipt = receipts.find((r) => String(r.receipt_id) === String(receiptId))

  // The receipt's items with what can still go back, priced at the PO cost
  // (then the receipt's cost, then the product's average cost).
  useEffect(() => {
    if (!receiptId) { setItems([]); return }
    let cancelled = false
    ;(async () => {
      try {
        const rec = await getReceipt(receiptId)
        const poId = rec?.header?.po_id || receipt?.po_id
        const po = poId ? await getPO(poId).catch(() => null) : null
        const poCost = Object.fromEntries((po?.lines || []).map((l) => [String(l.product_id), num(l.unit_cost)]))
        const lines = rec?.lines || []
        const pids = lines.map((l) => parseInt(l.product_id, 10)).filter(Boolean)
        const avail = pids.length ? (await getReceiptAvailability(receiptId, pids).catch(() => null))?.availability || {} : {}
        if (cancelled) return
        setItems(lines.map((l) => {
          const pid = String(l.product_id)
          const product = products.find((p) => String(p.product_id) === pid)
          const cost = poCost[pid] ?? (l.unit_cost != null ? num(l.unit_cost) : num(product?.avg_cost ?? product?.unit_cost))
          return { product_id: pid, sku: l.sku, name: l.name, received: num(l.quantity), available: avail[pid] ?? avail[Number(pid)] ?? null, qty: '', unit_cost: cost.toFixed(2), reason: '', area_id: '' }
        }))
        setScanned(false)
      } catch {
        if (!cancelled) setItems([])
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptId, products.length])

  useEffect(() => {
    if (!binTracking || !locationId) { setBins([]); return }
    getAreas(locationId).then((rows) => {
      const onlyBins = (rows || []).filter((a) => String(a.level || '').toLowerCase() === 'bin')
      setBins(onlyBins.length ? onlyBins : (rows || []))
      setItems((xs) => xs.map((x) => ({ ...x, area_id: '' })))
    }).catch(() => setBins([]))
  }, [locationId, binTracking])

  const setItem = (pid, patch) => setItems((xs) => xs.map((x) => (x.product_id === pid ? { ...x, ...patch } : x)))
  const returning = items.filter((i) => num(i.qty) > 0)
  const total = returning.reduce((s, i) => s + num(i.qty) * num(i.unit_cost), 0)
  const sortedReceipts = useMemo(() => [...receipts].sort((a, b) => num(b.receipt_id) - num(a.receipt_id)), [receipts])
  const binLabel = (b) => [String(b.department_name || '').trim(), b.path || b.code || b.name].filter(Boolean).join(' - ')

  const scan = (e) => {
    e.preventDefault()
    addCode(scanCode.trim())
  }

  // A code typed (a handheld scanner types it and presses Enter) or read by
  // the camera: one more of that item going back.
  const addCode = async (code) => {
    if (!code) return
    setScanError('')
    try {
      const prod = await resolveProductByCode(code)
      const pid = String(prod?.product_id || '')
      const item = items.find((i) => i.product_id === pid)
      if (!pid) throw new Error('No product has that code.')
      if (!item) throw new Error(`${prod?.name || 'That product'} isn’t on this receipt.`)
      setItem(pid, { qty: String(num(item.qty) + 1) })
      setScanned(true)
      setScanCode('')
      setTimeout(() => qtyRefs.current[pid]?.focus(), 0)
    } catch (err) {
      setScanError(err?.message || 'Could not read that code.')
    }
  }

  const save = async () => {
    setError('')
    if (!receiptId) { setError('Choose the receipt the goods came in on.'); return }
    if (!returning.length) { setError('Enter how many of each item are going back.'); return }
    if (!scanned) { setError('Scan at least one of the items going back before saving.'); scanRef.current?.focus(); return }
    const tooMany = returning.find((i) => i.available != null && num(i.qty) > num(i.available) + 1e-9)
    if (tooMany) { setError(`Only ${num(tooMany.available)} of ${tooMany.name || tooMany.sku} can go back from this receipt.`); return }
    if (binTracking && returning.some((i) => !i.area_id)) { setError('Choose the bin each returned item is coming out of.'); return }
    setSaving(true)
    try {
      const vendorId = receipt?.vendor_id
      const res = await saveVendorReturn({
        po_id: receipt?.po_id ? parseInt(receipt.po_id, 10) : null,
        vendor_id: vendorId ? parseInt(vendorId, 10) : null,
        receipt_id: parseInt(receiptId, 10),
        ref_receipt_id: parseInt(receiptId, 10),
        notes,
        lines: returning.map((i) => ({ product_id: parseInt(i.product_id, 10), quantity: num(i.qty), unit_cost: num(i.unit_cost), reason_code: i.reason || null, from_area_id: i.area_id && i.area_id !== 'none' ? parseInt(i.area_id, 10) : null, not_in_bin: i.area_id === 'none' })),
        total_amount: total,
      })
      notify(`${docNo('return', res?.return_no) || 'Return'} saved: ${returning.reduce((s, i) => s + num(i.qty), 0).toLocaleString()} units taken out of stock`)
      nav(res?.return_id ? `/purchase/vendor-returns/${res.return_id}` : '/purchase/vendor-returns')
    } catch (err) {
      setError(err.message || 'Could not save the return.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="ci-form-page">
      <DocHeader back={<BackLink to="/purchase/vendor-returns" label="Returns" />} title="Return goods to a vendor" meta="Choose the receipt they arrived on, scan what’s going back, and enter the quantities." />

      {error && <ErrorBanner message={error} />}

      <div className="ci-form-meta-card">
        <div className={`ci-form-meta-top ${binTracking ? 'two' : 'one'}`}>
          <label className="ci-form-field">
            <span>Arrived on receipt</span>
            <select value={receiptId} onChange={(e) => setReceiptId(e.target.value)}>
              <option value="">Choose a receipt…</option>
              {sortedReceipts.map((r) => (
                <option key={r.receipt_id} value={r.receipt_id}>
                  {docNo('receipt', r.receipt_no)} · {vendorNames[String(r.vendor_id)] || 'Vendor'}{r.po_no ? ` · ${docNo('po', r.po_no)}` : ''}{r.receipt_date ? ` · ${fmtDay(r.receipt_date)}` : ''}
                </option>
              ))}
            </select>
          </label>
          {binTracking && (
            <label className="ci-form-field">
              <span>Taking stock out of</span>
              <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
              </select>
            </label>
          )}
        </div>
        <form className="scan-row" onSubmit={scan}>
          <ScanLine size={16} aria-hidden="true" />
          <input ref={scanRef} value={scanCode} onChange={(e) => setScanCode(e.target.value)} placeholder="Scan or type an item code, then press Enter" disabled={!items.length} aria-label="Scan an item" />
          <ScanButton onScan={addCode} disabled={!items.length} />
          <button type="submit" className="ci-btn ci-btn-ghost ci-btn-sm" disabled={!items.length}>Add</button>
          <span className={`scan-state${scanned ? ' ok' : ''}`}>{scanned ? 'Scanned' : 'Scan at least one item'}</span>
        </form>
        {scanError && <p className="scan-error" role="alert">{scanError}</p>}
      </div>

      <div className="ci-form-lines-card">
        <div className="ci-form-lines-head">
          <div className="ci-form-lines-title">What’s going back</div>
          <span>{receipt ? `${vendorNames[String(receipt.vendor_id)] || 'Vendor'}${receipt.po_no ? ` · ${docNo('po', receipt.po_no)}` : ''}` : ''}</span>
        </div>
        {!receiptId ? (
          <p className="doc-empty">Choose a receipt above to see its items.</p>
        ) : items.length === 0 ? (
          <p className="doc-empty">Loading the receipt…</p>
        ) : (
          <div className="ci-form-table-wrap">
            <table className="ci-form-line-table compact">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Received</th>
                  <th className="num">Can go back</th>
                  <th className="num col-qty">Returning</th>
                  <th className="num">Unit cost</th>
                  {binTracking && <th>From bin</th>}
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.product_id}>
                    <td className="ci-form-cell-text">{it.name || it.sku}<span className="cell-sub">{it.sku}</span></td>
                    <td className="ci-form-cell-num">{it.received.toLocaleString()}</td>
                    <td className="ci-form-cell-num">{it.available == null ? '–' : num(it.available).toLocaleString()}</td>
                    <td>
                      <input ref={(el) => { qtyRefs.current[it.product_id] = el }} className="ci-form-cell-input num" value={it.qty} inputMode="decimal" placeholder="0" onChange={(e) => setItem(it.product_id, { qty: e.target.value })} aria-label={`Quantity of ${it.name || it.sku} going back`} />
                    </td>
                    <td className="ci-form-cell-num">{money(it.unit_cost)}</td>
                    {binTracking && (
                      <td>
                        <select className="ci-form-cell-select" value={it.area_id} onChange={(e) => setItem(it.product_id, { area_id: e.target.value })} aria-label={`Bin for ${it.name || it.sku}`}>
                          <option value="">Choose a bin…</option>
                          <option value="none">Not in a bin</option>
                          {bins.map((b) => <option key={b.area_id} value={b.area_id}>{binLabel(b)}</option>)}
                        </select>
                      </td>
                    )}
                    <td><input className="ci-form-cell-input" value={it.reason} placeholder="e.g. Damaged" onChange={(e) => setItem(it.product_id, { reason: e.target.value })} aria-label={`Reason for returning ${it.name || it.sku}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <label className="ci-form-field ci-form-note">
        <span>Note (optional)</span>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Collected by the vendor’s driver" />
      </label>

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button type="button" className="ci-btn ci-btn-primary" disabled={saving || !returning.length} onClick={save} title={!scanned ? 'Scan at least one item first' : undefined}>
            <Undo2 size={14} />{saving ? 'Saving…' : 'Save return'}
          </button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav('/purchase/vendor-returns')}>Cancel</button>
        </div>
        <div className="ci-form-action-right">
          <span className="doc-total">Value going back <strong>{money(total)}</strong></span>
        </div>
      </div>
    </div>
  )
}
